import { useEffect, useState } from 'react';
import { profileApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { StatusPill } from './StatusPills';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { InviteDialog } from './InviteDialog';

const TYPE_LABELS = {
  solicitor: 'Solicitor',
  legal_advocate: 'Legal advocate',
  support_worker: 'Support worker',
  counsellor: 'Counsellor',
  therapist: 'Therapist',
  gp_doctor: 'GP or doctor',
  family_member: 'Family member',
  friend: 'Friend',
  other: 'Other',
};

const PERM_LABELS = { read: 'Read only', read_write: 'Read and write' };

// Only these are still live/actionable — declined/left/expired/revoked are
// history, not something to keep cluttering this list.
const LIVE_STATUSES = ['pending', 'awaiting_acceptance', 'active'];


/**
 * Profile page section listing active invitees + the Invite button.
 */
export const SharedAccessCard = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // invitation being edited
  const [revoking, setRevoking] = useState(null); // invitation being revoked

  const load = () => {
    setLoading(true);
    profileApi
      .listInvitations()
      .then((r) => setInvitations((r.data?.invitations || []).filter((i) => LIVE_STATUSES.includes(i.status))))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Shared access could not be loaded.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDownload = async (inv) => {
    try {
      const res = await profileApi.downloadAuthorisation(inv.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `precious-data-authorisation-${inv.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('The authorisation could not be downloaded.');
    }
  };

  return (
    <Card className="bg-ground" data-testid="shared-access-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">
              Shared access
            </CardTitle>
            <CardDescription className="text-ink-55">
              People you have invited to read or act on your records. You control this, always.
            </CardDescription>
          </div>
          {!loading && invitations.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => setInviteOpen(true)}
              data-testid="shared-invite-btn"
            >
              Invite someone
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-ink-55">Loading</p>
        ) : invitations.length === 0 ? (
          <div className="rounded-lg bg-surface p-8 text-center space-y-3">
            <div aria-hidden="true" className="mx-auto w-[52px] h-[52px] rounded-lg bg-inset" />
            <p className="font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink">
              Only you can open your records
            </p>
            <p className="text-base text-ink-55 max-w-md mx-auto">
              If you invite a solicitor, support worker or someone you trust, they appear here.
            </p>
            <Button
              variant="secondary"
              className="mt-2"
              onClick={() => setInviteOpen(true)}
              data-testid="shared-invite-btn-empty"
            >
              Invite someone
            </Button>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="shared-invitations-list">
            {invitations.map((inv) => (
              <li
                key={inv.id}
                className="rounded-lg bg-surface p-4 space-y-2"
                data-testid={`shared-invitation-${inv.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 space-y-0.5">
                    <p className="font-medium text-ink text-[15px] truncate">
                      {inv.invitee_full_name || inv.invitee_email}
                    </p>
                    <p className="text-sm text-ink-55 truncate">
                      {inv.invitee_email}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <StatusPill tone="neutral" size="sm">
                        {TYPE_LABELS[inv.invitee_type] || inv.invitee_type}
                        {inv.invitee_type === 'other' && inv.invitee_type_other
                          ? `: ${inv.invitee_type_other}`
                          : ''}
                      </StatusPill>
                      <StatusPill tone="neutral" size="sm">
                        {PERM_LABELS[inv.permission] || inv.permission}
                      </StatusPill>
                      {inv.status === 'pending' && (
                        <StatusPill tone="needs" size="sm">
                          Waiting for them to sign up
                        </StatusPill>
                      )}
                      {inv.status === 'awaiting_acceptance' && (
                        <StatusPill tone="needs" size="sm">
                          Waiting for them to accept
                        </StatusPill>
                      )}
                      <span className="pd-mono text-ink-55">
                        Granted {inv.granted_at ? new Date(inv.granted_at).toLocaleDateString() : '—'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setEditing(inv)}
                      data-testid={`shared-edit-${inv.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleDownload(inv)}
                      data-testid={`shared-download-${inv.id}`}
                    >
                      Authorisation
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="text-breach-ink hover:text-breach-ink"
                      onClick={() => setRevoking(inv)}
                      data-testid={`shared-revoke-${inv.id}`}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onCreated={load} />
      {editing && (
        <EditPermissionDialog
          invitation={editing}
          onOpenChange={(v) => { if (!v) setEditing(null); }}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {revoking && (
        <RevokeDialog
          invitation={revoking}
          onOpenChange={(v) => { if (!v) setRevoking(null); }}
          onRevoked={() => { setRevoking(null); load(); }}
        />
      )}
    </Card>
  );
};


// =========================================================================
// Edit permission dialog
// =========================================================================
const EditPermissionDialog = ({ invitation, onOpenChange, onSaved }) => {
  const [permission, setPermission] = useState(invitation.permission);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await profileApi.updateInvitation(invitation.id, { permission });
      toast.success('Permission updated.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The change could not be saved.');
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-permission-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">Change what they can do</DialogTitle>
          <DialogDescription className="text-ink-55">
            Change what {invitation.invitee_full_name || invitation.invitee_email} can do in your account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Select value={permission} onValueChange={setPermission}>
            <SelectTrigger data-testid="edit-perm-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read only</SelectItem>
              <SelectItem value="read_write">Read and write</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || permission === invitation.permission}
                  data-testid="edit-perm-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// =========================================================================
// Revoke dialog with "also request deletion" checkbox
// =========================================================================
const RevokeDialog = ({ invitation, onOpenChange, onRevoked }) => {
  const [requestDeletion, setRequestDeletion] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const inviteeName = invitation.invitee_full_name || invitation.invitee_email;

  const submit = async () => {
    setRevoking(true);
    try {
      await profileApi.revokeInvitation(invitation.id, { request_deletion: requestDeletion });
      toast.success('Access revoked.');
      onRevoked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Access could not be revoked.');
      setRevoking(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="revoke-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
            Revoke access for {inviteeName}?
          </DialogTitle>
          <DialogDescription className="text-ink-55">
            They lose the ability to open your records straight away. Anything they
            downloaded while they had access is outside our control.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-3 rounded-lg bg-ground p-3 cursor-pointer">
          <Checkbox
            checked={requestDeletion}
            onCheckedChange={(v) => setRequestDeletion(!!v)}
            className="mt-0.5"
            data-testid="revoke-request-deletion"
          />
          <span className="text-sm text-ink-70 leading-relaxed">
            Also request that {inviteeName} deletes any copies of your records they may have
            saved or downloaded.
          </span>
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  disabled={revoking} data-testid="revoke-cancel">
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} disabled={revoking}
                  data-testid="revoke-confirm">
            {revoking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Revoke access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default SharedAccessCard;
